const { primitives } = require('./colors/primitives')
const {
  textVariables,
  contentVariables,
  backgroundVariables,
  strokeIconVariables,
  borderVariables,
} = require('./colors/variables')
const { animations, keyframes } = require('./animations')

const palette = {
  primary: {
    'sylvera-green': '#3ACB5A',
    'neon-blue': '#605CFF',
    lime: '#BFF102',
    25: '#F5F4FF',
    50: '#E9E9FF',
    100: '#D5D5FF',
    200: '#CBCAFF',
    300: '#B7B6FF',
    400: '#A5A4FF',
    500: '#9492FF',
    600: '#807DFF',
    700: '#7673FF',
    800: '#6D6AFF',
    900: '#605CFF',
    1000: '#4C48FF',
  },
  secondary: {
    water: '#48BCFD',
    lemon: '#FCE491',
    mauve: '#CEA2FA',
    burgundy: '#73043C',
    indigo: '#052185',
    gold: '#CAB674',
    50: '#EEEEFF',
    100: '#E2E2FF',
    200: '#D1D0F4',
    300: '#B9B8E4',
    400: '#A0A0D2',
    500: '#8584BE',
    600: '#7271AF',
    700: '#5D5C9C',
    800: '#4F4E8A',
    900: '#424177',
  },
  data: {
    red: '#F20C59',
    coral: '#FF8B47',
    orange: '#FFB914',
    yellow: '#FFE32A',
    green: '#3ACB5A',
    'dark-green': '#0F7435',
  },
  selection: {
    forest: '#01311D',
    white: '#FFFFFF',
    'off-white-1': '#FBFAF7',
    'off-white-2': '#F7F5EF',
    'off-white-3': '#EFEDE3',
  },
  black: {
    20: '#FAFAFA',
    25: '#F9F9F9',
    50: '#E9E9E9',
    100: '#D6D6D6',
    200: '#BABABA',
    300: '#9F9F9F',
    400: '#858585',
    500: '#6F6F6F',
    600: '#5C5C5C',
    700: '#434343',
    800: '#252525',
    900: '#000000',
  },
  purple: {
    'data-0': '#131233',
    'data-1': '#302E80',
    'data-2': '#4340B3',
    'data-3': '#605CFF',
    'data-4': '#807DFF',
    'data-5': '#A09DFF',
    'data-6': '#BFBEFF',
    'data-7': '#DFDEFF',
  },
  blue: {
    'data-0': '#014063',
    'data-1': '#0278BA',
    'data-2': '#0090E0',
    'data-3': '#00A3FF',
    'data-4': '#48BCFD',
    'data-5': '#84D3FF',
    'data-6': '#B1E3FF',
    'data-7': '#D7F0FF',
    'data-8': '#EBF6FD',
    25: '#EEEEFF',
    50: '#D7D7FF',
    100: '#CECEFF',
    200: '#8986FF',
    300: '#605CFF',
    400: '#4E4BD3',
    900: '#5f5cf6',
  },
  mauve: {
    'data-0': '#32273C',
    'data-1': '#3E314B',
    'data-2': '#67517D',
    'data-3': '#9071AF',
    'data-4': '#CEA2FA',
    'data-5': '#D8B5FB',
    'data-6': '#E7D1FD',
    'data-7': '#EEDFFE',
  },
  lemon: {
    'data-0': '#0D0A00',
    'data-1': '#322E1D',
    'data-2': '#655B3A',
    'data-3': '#978957',
    'data-4': '#CAB674',
    'data-5': '#FCE491',
    'data-6': '#FEF4D0',
    'data-7': '#FFFCF4',
  },
  green: {
    600: '#60b85f',
    500: '#5FD687',
    400: '#6ecb7d',
    300: '#78de89',
    200: '#a1e8ad',
    100: '#b8f1c1',
    50: '#d8f3d8',
    25: '#e4f7e5',
  },
  pink: {
    300: '#CEA2FA',
  },
  yellow: '#FFD646',
  gradients: {
    'background-gradient':
      'linear-gradient(77.74deg, #E6F7F8 0.69%, #EAF4F8 53.92%, #EDF6FF 102.97%)',
    'dark-background-gradient':
      'linear-gradient(90deg, #191d22 13.59%, #1e2227 94.66%)',
    'progress-ring-gradient':
      'conic-gradient(from 48.64deg at 49.67% 50%, #605CFF 0deg, #9492FF 54.38deg, #8986FF 166.87deg, #605CFF 320.63deg, #605CFF 360deg)',
    'compat-overlay-gradient':
      'linear-gradient(180.05deg, rgba(255, 255, 255, 0.33) 2.1%, rgba(255, 255, 255, 0.6) 158.16%)',
    'tooltip-content-gradient':
      'linear-gradient(99.19deg, #F0F0FF 27.02%, #F3ECFF 77.07%)',
    'provisional-rating-progress-ring-gradient':
      'conic-gradient(from 48.64deg at 49.67% 50%, #CEA2FA 0deg, #CEA2FA 54.38deg, #CEA2FA 166.87deg, #CEA2FA 320.63deg, #CEA2FA 360deg)',
  },
  'text-default-color': '#4F4F51',
  white: '#FFF',
  'scheme-purple': {
    0: '#131233',
    1: '#302E80',
    2: '#4340B3',
    3: '#605CFF',
    4: '#807DFF',
    5: '#A09DFF',
    6: '#BFBEFF',
    7: '#DFDEFF',
  },
  'scheme-blue': {
    0: '#0090E0',
    1: '#00A3FF',
    2: '#48BCFD',
    3: '#84D3FF',
    4: '#B1E3FF',
    5: '#D7F0FF',
    6: '#EBF6FD',
  },
  'scheme-mauve': {
    0: '#3E314B',
    1: '#67517D',
    2: '#9071AF',
    3: '#CEA2FA',
    4: '#D8B5FB',
    5: '#E7D1FD',
  },
  'scheme-lemon': {
    0: '#322E1D',
    1: '#655B3A',
    2: '#978957',
    3: '#CAB674',
    4: '#FCE491',
    5: '#FEF4D0',
  },

  'new-palette': {
    ...primitives,
  },
}

const screens = {
  'desktop-lg': '1680px',
  'desktop-md': '1280px',
  'desktop-sm': '1024px',
  tablet: '768px',
}

module.exports = {
  animations,
  keyframes,
  palette,
  screens,
  primitives,
  borderVariables,
  backgroundVariables,
  strokeIconVariables,
  textVariables,
  contentVariables,
}
